import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { workflows, messageTemplates, type Workflow, type MessageTemplate } from "@/mocks/workflowsData";

interface Props {
  onToggleWorkflow: (id: string) => void;
  onDeleteWorkflow: (id: string) => void;
  onDuplicateWorkflow: (id: string) => void;
}

type Tab = "workflows" | "templates";

export default function WorkflowsList({ onToggleWorkflow, onDeleteWorkflow, onDuplicateWorkflow }: Props) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>("workflows");
  const [workflowFilter, setWorkflowFilter] = useState<"All" | "Active" | "Draft" | "Disabled">("All");
  const [menuOpen, setMenuOpen] = useState<string | null>(null);

  const filteredWorkflows = workflowFilter === "All"
    ? workflows
    : workflows.filter((w) => w.status === workflowFilter);

  const statusConfig = {
    Active: { dot: "bg-green-500", text: "text-green-700", bar: "border-green-500" },
    Draft: { dot: "bg-amber-400", text: "text-amber-700", bar: "border-amber-400" },
    Disabled: { dot: "bg-gray-300", text: "text-gray-500", bar: "border-gray-300" },
  };

  const templateStatusConfig = {
    Approved: "bg-green-100 text-green-700",
    Pending: "bg-amber-100 text-amber-700",
    Draft: "bg-gray-100 text-gray-600",
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200">
      {/* Tabs */}
      <div className="flex items-center justify-between px-5 pt-5 pb-0">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setActiveTab("workflows")}
            className={`text-sm font-medium pb-3 border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
              activeTab === "workflows" ? "border-indigo-600 text-indigo-600" : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            Workflows
          </button>
          <button
            onClick={() => setActiveTab("templates")}
            className={`text-sm font-medium pb-3 border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
              activeTab === "templates" ? "border-indigo-600 text-indigo-600" : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            Message Templates
          </button>
        </div>

        {activeTab === "workflows" ? (
          <button
            onClick={() => navigate("/settings/workflows/new")}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line" />
            Create Workflow
          </button>
        ) : (
          <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-add-line" />
            New Template
          </button>
        )}
      </div>

      {/* Content */}
      {activeTab === "workflows" ? (
        <>
          {/* Sub-tabs + info row */}
          <div className="flex items-center justify-between px-5 pt-3 pb-2">
            <div className="flex items-center gap-1 bg-gray-100 rounded-full p-1">
              {(["All", "Active", "Draft", "Disabled"] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setWorkflowFilter(f)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                    workflowFilter === f ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {f}
                  {f === "Active" && (
                    <span className="ml-1 text-[10px] text-gray-400">
                      ({workflows.filter((w) => w.status === "Active").length})
                    </span>
                  )}
                </button>
              ))}
            </div>
            <span className="text-xs text-gray-400">
              {filteredWorkflows.length} workflow{filteredWorkflows.length !== 1 ? "s" : ""}
            </span>
          </div>

          {/* Workflow Cards */}
          <div className="px-5 pb-5 space-y-3">
            {filteredWorkflows.map((wf) => {
              const config = statusConfig[wf.status];
              return (
                <div
                  key={wf.id}
                  className={`bg-white rounded-xl border p-5 border-l-4 ${config.bar} relative`}
                >
                  {/* Top Row: Status + Toggle */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full ${config.dot}`} />
                      <span className={`text-xs font-medium ${config.text}`}>{wf.status}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => onToggleWorkflow(wf.id)}
                        className={`relative inline-flex items-center h-5 w-9 rounded-full transition-all duration-200 cursor-pointer ${
                          wf.status === "Active" ? "bg-green-500" : "bg-gray-200"
                        }`}
                      >
                        <span
                          className={`inline-block w-3.5 h-3.5 rounded-full bg-white shadow-sm transform transition-transform duration-200 ${
                            wf.status === "Active" ? "translate-x-[18px]" : "translate-x-[2px]"
                          }`}
                        />
                      </button>
                    </div>
                  </div>

                  {/* Second Row: Name + Trigger */}
                  <div className="flex items-center gap-2 mb-3">
                    <h4 className="font-bold text-gray-900">{wf.name}</h4>
                    <span className="bg-indigo-50 text-indigo-700 text-xs rounded px-2 py-0.5 whitespace-nowrap">
                      Trigger: {wf.trigger}
                    </span>
                  </div>

                  {/* Flow Preview Pills */}
                  <div className="flex items-center flex-wrap gap-1.5 mb-3">
                    {wf.flowSteps.map((step, si) => (
                      <span key={step.id} className="flex items-center gap-1">
                        {si > 0 && (
                          <i className="ri-arrow-right-line text-gray-300 text-xs" />
                        )}
                        <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${
                          step.type === "trigger" ? "bg-purple-50 text-purple-700" :
                          step.type === "condition" ? "bg-blue-50 text-blue-700" :
                          step.type === "delay" ? "bg-amber-50 text-amber-700" :
                          "bg-green-50 text-green-700"
                        }`}>
                          {step.label}
                        </span>
                      </span>
                    ))}
                  </div>

                  {/* Stats Row */}
                  <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
                    <span>Leads processed: <strong className="text-gray-700">{wf.leadsProcessed}</strong></span>
                    <span>Qualified: <strong className="text-gray-700">{wf.qualified}</strong> ({wf.leadsProcessed > 0 ? Math.round((wf.qualified / wf.leadsProcessed) * 100) : 0}%)</span>
                    <span>Avg response time: <strong className="text-gray-700">{wf.avgResponseTime}</strong></span>
                  </div>

                  {/* Action Row */}
                  <div className="flex items-center gap-2 relative">
                    <button
                      onClick={() => navigate(`/settings/workflows/${wf.id}/edit`)}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-indigo-200 text-indigo-600 text-xs font-medium hover:bg-indigo-50 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-pencil-line" /> Edit
                    </button>
                    <button
                      onClick={() => onDuplicateWorkflow(wf.id)}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-gray-200 text-gray-500 text-xs font-medium hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-file-copy-line" /> Duplicate
                    </button>

                    {/* 3-dot menu */}
                    <div className="relative ml-auto">
                      <button
                        onClick={() => setMenuOpen(menuOpen === wf.id ? null : wf.id)}
                        className="w-7 h-7 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 cursor-pointer transition-colors"
                      >
                        <i className="ri-more-2-fill text-sm" />
                      </button>
                      {menuOpen === wf.id && (
                        <>
                          <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(null)} />
                          <div className="absolute right-0 top-8 w-40 bg-white rounded-xl border border-gray-200 shadow-lg z-20 py-1">
                            <button
                              onClick={() => { navigate(`/settings/workflows/${wf.id}/edit`); setMenuOpen(null); }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                            >
                              <i className="ri-pencil-line text-gray-400" /> Edit
                            </button>
                            <button
                              onClick={() => { onDuplicateWorkflow(wf.id); setMenuOpen(null); }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                            >
                              <i className="ri-file-copy-line text-gray-400" /> Duplicate
                            </button>
                            <button
                              onClick={() => { onToggleWorkflow(wf.id); setMenuOpen(null); }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                            >
                              <i className="ri-toggle-line text-gray-400" /> {wf.status === "Active" ? "Disable" : "Enable"}
                            </button>
                            <hr className="my-1 border-gray-100" />
                            <button
                              onClick={() => { onDeleteWorkflow(wf.id); setMenuOpen(null); }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors whitespace-nowrap cursor-pointer"
                            >
                              <i className="ri-delete-bin-line" /> Delete
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {filteredWorkflows.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                <i className="ri-flow-chart text-3xl mb-2 block" />
                <p className="text-sm">No workflows found</p>
              </div>
            )}
          </div>
        </>
      ) : (
        /* Message Templates Tab */
        <div className="px-5 pb-5 pt-3 space-y-3">
          {messageTemplates.map((tpl) => (
            <div key={tpl.id} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <h4 className="font-semibold text-gray-900 text-sm">{tpl.name}</h4>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${templateStatusConfig[tpl.status]}`}>
                    {tpl.status}
                  </span>
                  <span className="text-xs text-gray-400">{tpl.language}</span>
                </div>
                <div className="flex items-center gap-1">
                  <button className="w-7 h-7 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 cursor-pointer transition-colors">
                    <i className="ri-pencil-line text-sm" />
                  </button>
                  <button className="w-7 h-7 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 cursor-pointer transition-colors">
                    <i className="ri-file-copy-line text-sm" />
                  </button>
                  <button className="w-7 h-7 rounded-lg hover:bg-red-50 flex items-center justify-center text-gray-400 hover:text-red-500 cursor-pointer transition-colors">
                    <i className="ri-delete-bin-line text-sm" />
                  </button>
                </div>
              </div>

              {/* Message Preview */}
              <div className="bg-gray-50 rounded-lg p-3 mb-3">
                <p className="text-sm text-gray-700 whitespace-pre-line">{tpl.message}</p>
              </div>

              {/* Variables + Usage */}
              <div className="flex items-center gap-3">
                {tpl.variables.length > 0 && (
                  <div className="flex items-center gap-1.5">
                    {tpl.variables.map((v) => (
                      <span key={v} className="bg-indigo-50 text-indigo-700 text-xs px-1.5 py-0.5 rounded">
                        {"{"}{v}{"}"}
                      </span>
                    ))}
                  </div>
                )}
                <span className="text-xs text-gray-400 ml-auto">Used in {tpl.usageCount} workflow{tpl.usageCount !== 1 ? "s" : ""}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}